import React, { useState } from 'react';
import { Clock, Play, Pause, Save } from 'lucide-react';
import { onboardingIntegrationClient } from '../../../api/onboardingIntegrationClient';

interface SimpleTimeTrackingProps {
  projectId: string;
  userRole: 'client' | 'vendor' | 'admin';
}

export const SimpleTimeTracking: React.FC<SimpleTimeTrackingProps> = ({ 
  projectId, 
  userRole 
}) => {
  const [activeTimer, setActiveTimer] = useState<string | null>(null);
  const [timeEntry, setTimeEntry] = useState({
    taskId: '',
    description: '',
    duration: 0
  });

  const startTimer = (taskId: string) => {
    setActiveTimer(taskId);
    console.log(`Timer started for task: ${taskId}`);
  };

  const stopTimer = () => {
    setActiveTimer(null);
    console.log('Timer stopped');
  };

  const logTime = async () => {
    if (!timeEntry.taskId || !timeEntry.duration) return;
    
    try {
      await onboardingIntegrationClient.logTime({
        projectId,
        taskId: timeEntry.taskId,
        duration: timeEntry.duration,
        description: timeEntry.description
      });
      
      setTimeEntry({ taskId: '', description: '', duration: 0 });
      console.log('Time logged successfully');
    } catch (error) {
      console.error('Failed to log time:', error);
    }
  };

  return (
    <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <Clock className="w-5 h-5 text-[#4267A1] mr-2" />
        Time Tracking
      </h3>
      
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {['Setup Documentation', 'Data Migration', 'SSO Configuration'].map((task, index) => (
            <div key={index} className="p-3 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">{task}</h4>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">2.5h logged</span>
                {activeTimer === `task-${index}` ? (
                  <button
                    onClick={stopTimer}
                    className="flex items-center space-x-1 px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                  >
                    <Pause className="w-3 h-3" />
                    <span>Stop</span>
                  </button>
                ) : (
                  <button
                    onClick={() => startTimer(`task-${index}`)}
                    className="flex items-center space-x-1 px-2 py-1 bg-[#8A9150] text-white text-xs rounded hover:bg-[#A5B366]"
                  >
                    <Play className="w-3 h-3" />
                    <span>Start</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <div className="border-t border-gray-200 pt-4">
          <h4 className="font-medium text-gray-900 mb-3">Manual Time Entry</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <select
              value={timeEntry.taskId}
              onChange={(e) => setTimeEntry({...timeEntry, taskId: e.target.value})}
              className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#4267A1] focus:border-transparent"
            >
              <option value="">Select task</option>
              <option value="task-1">Setup Documentation</option>
              <option value="task-2">Data Migration</option>
              <option value="task-3">SSO Configuration</option>
            </select>
            
            <input
              type="number"
              value={timeEntry.duration}
              onChange={(e) => setTimeEntry({...timeEntry, duration: Number(e.target.value)})}
              placeholder="Minutes"
              className="p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#4267A1] focus:border-transparent"
            />
            
            <button
              onClick={logTime}
              disabled={!timeEntry.taskId || !timeEntry.duration}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>Log</span>
            </button>
          </div>
          
          <input
            type="text"
            value={timeEntry.description}
            onChange={(e) => setTimeEntry({...timeEntry, description: e.target.value})}
            placeholder="What did you work on?"
            className="w-full mt-3 p-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#4267A1] focus:border-transparent"
          />
        </div>
        
        <div className="text-xs text-gray-500">
          <p>• Time entries update resource capacity automatically</p>
          <p>• All time tracking data feeds into project analytics</p>
        </div>
      </div>
    </div>
  );
};
